import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { ArrowLeft, Camera } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import React from 'react';
import {
    backButtonStyle,
    buttonContainer,
    cameraSection,
    captureContainer,
    captureContent,
    errorContainer,
    errorText,
    instructionItem,
    instructionLabel,
    instructionText,
    instructionsPanel,
    loadingContainer,
    overlayContainer,
    scanButton,
    stepDescription,
    stepIndicator,
    stepTitle,
    videoContainer,
} from './/VisualHealthAnalysisStyle';
import CameraOverlay from './CameraOverlay';
import { useCameraPermission } from 'hooks/useCameraPermission';
import { captureImageFromVideo } from 'utils/imageUtils';
import { BodyPartType } from 'utils/visualUtils';
import { useNavigate } from 'react-router-dom';

interface CameraCaptureProps {
    type: BodyPartType;
    stepNumber: number;
    onCapture: (base64Image: string) => void;
    onBack: () => void;
    isLastStep?: boolean;
}

const STEP_CONFIG = {
    eyes: {
        title: 'EYES',
        description: 'This step scans your eyes for analysis.',
        eyePlacement: 'Move your face slightly until each of your eyes aligns perfectly within the two smaller inner circles.',
        holdStill: 'Once your eyes are correctly placed, click the button below. Do not move until the scan is completed.',
    },
    lips: {
        title: 'LIPS',
        description: 'This step focuses on scanning the color and condition of your lips.',
        eyePlacement: 'Align your lips with the lip-shaped outline. Keep lips relaxed.',
        holdStill: 'Hold still and click scan.',
    },
    tongue: {
        title: 'TONGUE',
        description: 'This step requires a clear view of your tongue.',
        eyePlacement: 'Open your mouth and align your tongue with the outline.',
        holdStill: 'Hold still and click scan.',
    },
    nails: {
        title: 'NAILS',
        description: 'This step scans the surface and nail bed of your fingernails.',
        eyePlacement: 'Place your fingers so all nails are visible.',
        holdStill: 'Hold still and click scan.',
    },
};

export default function CameraCapture({
    type,
    stepNumber,
    onCapture,
    onBack,
    isLastStep = false,
}: CameraCaptureProps) {
    const { permissionState, error, requestPermission, stopCamera, isLoading } = useCameraPermission();
    const videoRef = useRef<HTMLVideoElement>(null);
    const [stream, setStream] = useState<MediaStream | null>(null);
    const [isCapturing, setIsCapturing] = useState(false);

    // New state for camera selection
    const [showCameraSelect, setShowCameraSelect] = useState(true);
    const [selectedCamera, setSelectedCamera] = useState<'user' | 'environment'>('user');

    const config = STEP_CONFIG[type];
    const navigate = useNavigate();

    // Start camera with facingMode
    const startCamera = async () => {
        try {
            await requestPermission();
            const camStream = await navigator.mediaDevices.getUserMedia({
                video: {
                    width: 640,
                    height: 480,
                    facingMode: selectedCamera,
                },
            });
            setStream(camStream);
            if (videoRef.current) {
                videoRef.current.srcObject = camStream;
            }
        } catch (err) {
            console.error('Camera init error:', err);
        }
    };

    // Start camera after selection
    useEffect(() => {
        if (!showCameraSelect) {
            startCamera();
        }

        return () => {
            stopCamera();
            if (stream) {
                stream.getTracks().forEach((t) => t.stop());
            }
        };
    }, [showCameraSelect, selectedCamera]);

    const handleCapture = () => {
        if (!videoRef.current) return;
        try {
            setIsCapturing(true);
            const base64Image = captureImageFromVideo(videoRef.current, 0.8);
            setTimeout(() => {
                onCapture(base64Image);
                if (isLastStep) {
                    stopCamera();
                    setTimeout(() => navigate('/camera-scan'), 800);
                } else {
                    setIsCapturing(false);
                }
            }, 500);
        } catch (err) {
            console.error('Failed to capture image:', err);
            setIsCapturing(false);
            alert('Failed to capture image. Please try again.');
        }
    };

    const handleBackClick = () => {
        stopCamera();
        onBack();
    };

    // Show camera selection
    if (showCameraSelect) {
        return (
            <Box sx={captureContainer}>
                <Box sx={{ textAlign: 'center', mt: 6 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Select Camera</Typography>
                    <Button
                        variant="contained"
                        sx={{ m: 1 }}
                        onClick={() => { setSelectedCamera('user'); setShowCameraSelect(false); }}
                    >
                        Front Camera
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ m: 1 }}
                        onClick={() => { setSelectedCamera('environment'); setShowCameraSelect(false); }}
                    >
                        Back Camera
                    </Button>
                    <Box sx={{ mt: 2 }}>
                        <Button variant="text" onClick={handleBackClick}>Go Back</Button>
                    </Box>
                </Box>
            </Box>
        );
    }

    // Loading State
    if (isLoading || !stream) {
        return (
            <Box sx={captureContainer}>
                <Box sx={loadingContainer}>
                    <CircularProgress size={60} />
                    <Typography variant="h6">Starting camera...</Typography>
                </Box>
            </Box>
        );
    }

    // Permission Error
    if (error || permissionState === 'denied') {
        return (
            <Box sx={captureContainer}>
                <Box sx={errorContainer}>
                    <Camera size={64} color="#D32F2F" />
                    <Typography sx={errorText}>{error || 'Camera access denied'}</Typography>
                    <Button variant="outlined" onClick={requestPermission}>
                        Try Again
                    </Button>
                    <Button variant="text" onClick={handleBackClick} sx={{ mt: 1 }}>
                        Go Back
                    </Button>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={captureContainer}>
            <Box sx={captureContent}>
                {/* Camera Section */}
                <Box sx={cameraSection}>
                    <Box sx={videoContainer}>
                        <video
                            ref={videoRef}
                            autoPlay
                            playsInline
                            muted
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                            }}
                        />
                        {/* Overlay */}
                        <Box sx={overlayContainer}>
                            <CameraOverlay type={type} />
                        </Box>
                    </Box>
                    {/* Buttons */}
                    <Box sx={buttonContainer}>
                        <Button
                            variant="outlined"
                            startIcon={<ArrowLeft size={18} />}
                            onClick={handleBackClick}
                            sx={backButtonStyle}
                        >
                            Back
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleCapture}
                            disabled={isCapturing || !stream}
                            sx={scanButton}
                        >
                            {isCapturing ? (
                                <>
                                    <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                                    Scanning...
                                </>
                            ) : isLastStep ? (
                                'Click to vital scan'
                            ) : (
                                'Scan and Next'
                            )}
                        </Button>
                    </Box>
                </Box>
                {/* Instructions */}
                <Box sx={instructionsPanel}>
                    <Typography sx={stepIndicator}>STEP {stepNumber} OF 4</Typography>
                    <Typography sx={stepTitle}>{config.title}</Typography>
                    <Typography sx={stepDescription}>{config.description}</Typography>
                    <Typography sx={instructionLabel}>Instructions:</Typography>
                    <Box sx={instructionItem}>
                        <Typography sx={instructionText}>
                            <strong>Placement:</strong> {config.eyePlacement}
                        </Typography>
                    </Box>
                    <Box sx={instructionItem}>
                        <Typography sx={instructionText}>
                            <strong>Hold Still:</strong> {config.holdStill}
                        </Typography>
                    </Box>
                    {stream && (
                        <Alert severity="success" sx={{ mt: 3 }}>
                            Camera is active. Position yourself according to the guide.
                        </Alert>
                    )}
                </Box>
            </Box>
        </Box>
    );
}
